import { useState, useEffect } from 'react';
import {
  createApontamento,
  updateApontamento,
  getValoresHora,
  listApontamentos,
  Apontamento,
  ApontamentoCreateData,
  ValoresHora,
} from '../../services/apontamentoService';
import { Loader2, X, Clock } from 'lucide-react';
import { toLocalISODate } from '../../utils/dateUtils';
import { Button } from '../../components/ui/button';
import api from '../../services/api';

interface ApontamentoFormProps {
  ticketId: string;
  apontamento?: Apontamento;
  onClose: () => void;
  onSuccess: () => void;
}

export const ApontamentoForm = ({
  ticketId,
  apontamento,
  onClose,
  onSuccess,
}: ApontamentoFormProps) => {
  const isEditing = !!apontamento;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [valoresHora, setValoresHora] = useState<ValoresHora | null>(null);
  const [usuarios, setUsuarios] = useState<Array<{ id: string; name: string }>>([]);
  const [horariosOcupados, setHorariosOcupados] = useState<Array<{ id: string; horaInicio: string; horaFim: string }>>([]);

  useEffect(() => {
    getValoresHora()
      .then(setValoresHora)
      .catch((err) => console.error('Erro ao buscar valores de hora:', err));

    api
      .get('/admin/users', { params: { limit: '100' } })
      .then((res) => setUsuarios(res.data.data || []))
      .catch((err) => console.error('Erro ao buscar usuários:', err));
  }, []);

  // Funções auxiliares para extrair data/hora sem conversão de timezone
  const extractDate = (isoString: string): string => {
    // Extrai apenas YYYY-MM-DD da string ISO, ignorando timezone
    return isoString.substring(0, 10);
  };

  const extractTime = (isoString: string): string => {
    // Extrai apenas HH:mm da string ISO, ignorando timezone
    // Se vier com Z ou offset, pega a parte antes do T e depois os primeiros 5 chars
    const timePart = isoString.split('T')[1];
    return timePart ? timePart.substring(0, 5) : '';
  };

  // Data local (YYYY-MM-DD) sem depender de UTC — evita "data futura" quando o
  // fuso (ex.: America/Sao_Paulo) já virou o dia no horário UTC.
  const todayLocal = () => toLocalISODate();

  // Form data
  const [formData, setFormData] = useState({
    data: apontamento?.data ? extractDate(apontamento.data) : todayLocal(),
    horaInicio: apontamento?.horaInicio ? extractTime(apontamento.horaInicio) : '',
    horaFim: apontamento?.horaFim ? extractTime(apontamento.horaFim) : '',
    tipoHora: (apontamento?.tipoHora as 'HTC' | 'HTE' | 'PROJETO') || 'HTC',
    descricao: apontamento?.descricao || '',
    userId: apontamento?.userId || '',
  });

  // Busca horários já ocupados do atendente selecionado na data escolhida
  useEffect(() => {
    if (!formData.data) return;

    const filtro = { dataInicio: formData.data, dataFim: formData.data };
    if (formData.userId) {
      (filtro as any).userId = formData.userId;
    }

    listApontamentos(filtro)
      .then((lista) =>
        setHorariosOcupados(
          lista
            .filter((a) => a.id !== apontamento?.id)
            .map((a) => ({
              id: a.id,
              horaInicio: a.horaInicio,
              horaFim: a.horaFim,
            }))
        )
      )
      .catch((err) => console.error('Erro ao buscar horários ocupados:', err));
  }, [formData.data, formData.userId, apontamento?.id]);

  // Calcular tempo total
  const calcularTempoTotal = () => {
    if (!formData.horaInicio || !formData.horaFim) return null;

    const [horaIni, minIni] = formData.horaInicio.split(':').map(Number);
    const [horaFim, minFim] = formData.horaFim.split(':').map(Number);

    const minutosInicio = horaIni * 60 + minIni;
    const minutosFim = horaFim * 60 + minFim;

    if (minutosFim <= minutosInicio) return null;

    const totalMinutos = minutosFim - minutosInicio;
    const horas = Math.floor(totalMinutos / 60);
    const minutos = totalMinutos % 60;

    return { horas, minutos, totalMinutos };
  };

  // Calcular valor estimado
  const calcularValorEstimado = () => {
    const tempo = calcularTempoTotal();
    if (!tempo || !valoresHora) return null;

    const valorHora = valoresHora[formData.tipoHora] ?? 0;
    const valorTotal = (tempo.totalMinutos / 60) * valorHora;

    return valorTotal;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validações
    if (!formData.data) {
      setError('Data é obrigatória');
      return;
    }
    if (!formData.horaInicio || !formData.horaFim) {
      setError('Horário de início e fim são obrigatórios');
      return;
    }
    if (formData.descricao.trim().length < 20) {
      setError('Descrição deve ter no mínimo 20 caracteres');
      return;
    }

    const tempo = calcularTempoTotal();
    if (!tempo) {
      setError('Hora fim deve ser posterior à hora início');
      return;
    }

    try {
      setLoading(true);

      // Montar strings ISO sem conversão de timezone
      // Formato: YYYY-MM-DDTHH:mm:ss (sem Z ou offset, será tratado como local pelo backend)
      const dataISO = formData.data;
      const horaInicioISO = `${formData.data}T${formData.horaInicio}:00`;
      const horaFimISO = `${formData.data}T${formData.horaFim}:00`;

      const data: ApontamentoCreateData = {
        ticketId,
        userId: formData.userId,
        data: dataISO,
        horaInicio: horaInicioISO,
        horaFim: horaFimISO,
        tipoHora: formData.tipoHora,
        descricao: formData.descricao.trim(),
      };

      if (isEditing && apontamento) {
        await updateApontamento(apontamento.id, {
          data: dataISO,
          horaInicio: horaInicioISO,
          horaFim: horaFimISO,
          tipoHora: formData.tipoHora,
          descricao: formData.descricao.trim(),
        });
      } else {
        await createApontamento(data);
      }

      onSuccess();
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Erro ao salvar apontamento';
      setError(errorMessage);
      console.error('Erro ao salvar apontamento:', err);
    } finally {
      setLoading(false);
    }
  };

  const tempo = calcularTempoTotal();
  const valorEstimado = calcularValorEstimado();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">
            {isEditing ? 'Editar Apontamento' : 'Novo Apontamento'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-foreground" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Erro */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Data */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Data *
            </label>
            <input
              type="date"
              value={formData.data}
              onChange={(e) => setFormData({ ...formData, data: e.target.value })}
              max={todayLocal()}
              className="w-full px-4 py-2 bg-background border border-input rounded-lg text-foreground focus:ring-2 focus:ring-brand focus:border-transparent"
              required
            />
            <p className="text-xs text-muted-foreground mt-1">Data não pode ser futura</p>
          </div>

          {/* Atendente */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Atendente *
            </label>
            <select
              value={formData.userId}
              onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
              className="w-full px-4 py-2 bg-background border border-input rounded-lg text-foreground focus:ring-2 focus:ring-brand focus:border-transparent"
              required
            >
              <option value="">Selecione um atendente</option>
              {usuarios.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>

          {/* Preview de horários ocupados */}
          {formData.userId && formData.data && (
            <div className="border border-border rounded-lg p-4 bg-muted/30">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm font-medium text-foreground">Horários ocupados neste dia</p>
              </div>
              {horariosOcupados.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nenhum horário registrado — dia livre.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {horariosOcupados.map((h) => (
                    <span
                      key={h.id}
                      className="px-2 py-1 bg-background border border-border rounded-md text-xs text-muted-foreground"
                    >
                      {h.horaInicio.substring(11, 16)} – {h.horaFim.substring(11, 16)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Horários */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Hora Início *
              </label>
              <input
                type="time"
                value={formData.horaInicio}
                onChange={(e) => setFormData({ ...formData, horaInicio: e.target.value })}
                className="w-full px-4 py-2 bg-background border border-input rounded-lg text-foreground focus:ring-2 focus:ring-brand focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Hora Fim *
              </label>
              <input
                type="time"
                value={formData.horaFim}
                onChange={(e) => setFormData({ ...formData, horaFim: e.target.value })}
                className="w-full px-4 py-2 bg-background border border-input rounded-lg text-foreground focus:ring-2 focus:ring-brand focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* Tempo Total Calculado */}
          {tempo && (
            <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-300">Tempo Total</p>
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {tempo.horas}h {tempo.minutos > 0 && `${tempo.minutos}min`}
                  </p>
                </div>
                {valorEstimado !== null && (
                  <div className="text-right">
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-300">Valor Estimado</p>
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      }).format(valorEstimado)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tipo de Hora */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Tipo de Hora *
            </label>
            <select
              value={formData.tipoHora}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  tipoHora: e.target.value as 'HTC' | 'HTE' | 'PROJETO',
                })
              }
              className="w-full px-4 py-2 bg-background border border-input rounded-lg text-foreground focus:ring-2 focus:ring-brand focus:border-transparent"
              required
            >
              <option value="HTC">HTC - Hora Técnica Comercial{valoresHora ? ` (R$ ${valoresHora.HTC}/h)` : ''}</option>
              <option value="HTE">HTE - Hora Técnica Emergencial{valoresHora ? ` (R$ ${valoresHora.HTE}/h)` : ''}</option>
            </select>
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Descrição Técnica *
            </label>
            <textarea
              value={formData.descricao}
              onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              placeholder="Descreva detalhadamente as atividades realizadas..."
              rows={5}
              className="w-full px-4 py-2 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-brand focus:border-transparent resize-none"
              required
              minLength={20}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Mínimo 20 caracteres ({formData.descricao.length}/20)
            </p>
          </div>

          {/* Botões */}
          <div className="flex items-center justify-end gap-4 pt-4 border-t border-border">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading || !tempo}>
              {loading && <Loader2 className="animate-spin" />}
              {isEditing ? 'Atualizar' : 'Criar'} Apontamento
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
